/**
 * ThinkingBanner Component
 * Collapsible banner showing the AI's thinking process (tool calls, delegations, reasoning)
 * Displayed above the response text when reveal_thinking is enabled on the chatbot.
 */

import { useState, useEffect, useRef } from 'react';
import {
  SearchOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../../../hooks/useLanguage';
import type { ThinkingStep } from '../../../types/models';
import './ThinkingBanner.css';

const STEP_ICONS: Record<string, React.ReactNode> = {
  tool_call: <SearchOutlined />,
  tool_result: <CheckCircleOutlined />,
  skill_activation: <ThunderboltOutlined />,
  delegation: <SwapOutlined />,
  processing: <LoadingOutlined />,
  reasoning: <ThunderboltOutlined />,
};

const TOOL_ICONS: Record<string, React.ReactNode> = {
  knowledge_search: <SearchOutlined />,
  read_file: <FileTextOutlined />,
  web_search: <SearchOutlined />,
};

function getStepIcon(step: ThinkingStep): React.ReactNode {
  // For tool_call/tool_result, try to match the tool name in the label
  if (step.step_type === 'tool_call' || step.step_type === 'tool_result') {
    for (const [toolName, icon] of Object.entries(TOOL_ICONS)) {
      if (step.label.toLowerCase().includes(toolName)) {
        return icon;
      }
    }
  }
  return STEP_ICONS[step.step_type] || <ThunderboltOutlined />;
}

interface ThinkingBannerProps {
  steps: ThinkingStep[];
  streaming?: boolean;
}

export default function ThinkingBanner({ steps, streaming = false }: ThinkingBannerProps) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(streaming);
  const stepsEndRef = useRef<HTMLDivElement>(null);
  const [expandedDetails, setExpandedDetails] = useState<Set<number>>(new Set());

  // Auto-expand while streaming, collapse when done
  useEffect(() => {
    if (streaming) {
      setExpanded(true);
    }
  }, [streaming]);

  // Auto-scroll to latest step while streaming
  useEffect(() => {
    if (streaming && expanded && stepsEndRef.current) {
      stepsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [steps.length, streaming, expanded]);

  if (steps.length === 0) return null;

  const toggleDetail = (index: number) => {
    setExpandedDetails(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const summaryText = t('chat.thinking.stepsCount', { count: steps.length })
    || `${steps.length} steps`;

  return (
    <div className={`thinking-banner ${streaming ? 'thinking-banner--streaming' : ''}`}>
      <button
        className="thinking-banner__header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="thinking-banner__icon">
          {streaming ? <LoadingOutlined spin /> : <ThunderboltOutlined />}
        </span>
        <span className="thinking-banner__title">
          {t('chat.thinking.title') || 'Thinking'}
        </span>
        <span className="thinking-banner__summary">
          {summaryText}
        </span>
        <span className="thinking-banner__chevron">
          {expanded ? <DownOutlined /> : <RightOutlined className="rtl-flip" />}
        </span>
      </button>

      {expanded && (
        <div className="thinking-banner__steps">
          {steps.map((step, idx) => (
            <div key={idx} className={`thinking-step thinking-step--${step.step_type}`}>
              <span className="thinking-step__icon">{getStepIcon(step)}</span>
              <span className="thinking-step__label" dir="auto">
                {step.label}
              </span>
              {step.detail && (
                <button
                  className="thinking-step__detail-toggle"
                  onClick={() => toggleDetail(idx)}
                  aria-expanded={expandedDetails.has(idx)}
                >
                  {expandedDetails.has(idx) ? <DownOutlined /> : <RightOutlined className="rtl-flip" />}
                </button>
              )}
              {step.detail && expandedDetails.has(idx) && (
                <pre className="thinking-step__detail" dir="auto">{step.detail}</pre>
              )}
            </div>
          ))}
          <div ref={stepsEndRef} />
        </div>
      )}
    </div>
  );
}
